'use client';

/**
 * SplashScreen — initial-load fallback shown while CoachApp wires up state.
 *
 * Replaces the old inline <div>Logo</div> placeholder with something that
 * communicates "loading", not just "stuck on a logo". Subtle pulse animation
 * so the user knows the app is alive.
 *
 * Why a separate component:
 *   - Reusable for other initial-load states later (e.g. cloud sync)
 *   - Keeps CoachApp.js lean
 *   - Can be styled with the legacy theme for color consistency
 *
 * Intentionally not theme-dependent (no useLegacyC()):
 *   The theme provider mounts at the same time as everything else. If the
 *   theme provider itself fails (or hasn't mounted yet during SSR), this
 *   should still render correctly. So inline styles only.
 */

import React from 'react';

export interface SplashScreenProps {
  /** Optional message under the logo. Default: "Lädt deine Daten..." */
  message?: string;
}

export function SplashScreen({ message = 'Lädt deine Daten...' }: SplashScreenProps) {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0A0D12',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 18,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div
        style={{
          fontSize: 32,
          fontWeight: 800,
          letterSpacing: -0.8,
          animation: 'coachai-splash-pulse 1.6s ease-in-out infinite',
        }}
      >
        <span style={{ color: '#FFFFFF' }}>Athl</span>
        <span style={{ color: '#9BC22E' }}>yn</span>
      </div>
      <div style={{ fontSize: 13, color: '#A6ACB5' }}>{message}</div>
      <style>{`
        @keyframes coachai-splash-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.7; transform: scale(0.97); }
        }
      `}</style>
    </div>
  );
}
